import React, { useEffect, useState } from "react";
import { fetchDailyHoursWorked } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { formatHoursMinutes } from "../utils/time";
import { useTranslation } from "react-i18next";

const DailyHoursWorkedChart = ({ date, onLoadingChange }) => {
  const [chartData, setChartData] = useState([]);
  const [workcellNames, setWorkcellNames] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    if (!date) {
      setChartData([]);
      setWorkcellNames({});
      setIsLoading(false);
      onLoadingChange?.(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    onLoadingChange?.(true);
    fetchDailyHoursWorked(date)
      .then(apiData => {
        if (cancelled) return;
        if (!apiData || apiData.length === 0) {
          setChartData([]);
          return;
        }

        // Get unique workcell IDs
        const workcells = [...new Set(apiData.map((entry) => entry.workcell_id))];
        // Build mapping for workcell names
        const workcellNamesMap = {};
        apiData.forEach((entry) => {
          workcellNamesMap[entry.workcell_id] = entry.workcell_name;
        });
        setWorkcellNames(workcellNamesMap);

        // Create a full 24-hour dataset with default 0 values for each workcell
        const fullDayData = Array.from({ length: 24 }, (_, i) => {
          const hour = i.toString().padStart(2, "0");
          const entry = { hour };
          workcells.forEach((workcell) => {
            entry[workcell] = 0;
          });
          return entry;
        });

        // Merge API data into the full dataset
        apiData.forEach((entry) => {
          const index = fullDayData.findIndex((d) => d.hour === entry.hour);
          if (index !== -1) {
            // Use 'hours_worked' or fallback to 'work_done'
            fullDayData[index][entry.workcell_id] = entry.hours_worked || entry.work_done;
          }
        });

        setChartData(fullDayData);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Error fetching hourly data:', error);
        setChartData([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
        onLoadingChange?.(false);
      });
    return () => { cancelled = true; };
  }, [date]);

  // Compute sorted workcells based on total hours
  const sortedWorkcells = chartData.length > 0
    ? Object.keys(chartData[0])
        .filter(key => key !== "hour")
        .sort((a, b) => {
          const totalA = chartData.reduce((sum, entry) => sum + (entry[a] || 0), 0);
          const totalB = chartData.reduce((sum, entry) => sum + (entry[b] || 0), 0);
          return totalB - totalA;
        })
    : [];

  // Compute all workcell IDs sorted numerically for color mapping
  const allWorkcellIds = chartData.length > 0
    ? Object.keys(chartData[0])
        .filter(key => key !== "hour")
        .map(Number)
        .sort((a, b) => a - b)
    : [];

  if (isLoading) return <div>{t("common.loading")}</div>;
  if (chartData.length === 0) {
    return <div>{t("common.noData")}</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData}>
        <XAxis dataKey="hour" />
        <YAxis
          label={{
            value: t("daily.hours.chart.axisLabel"),
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle" }
          }}
        />
        <Tooltip formatter={(value) => (typeof value === 'number' ? formatHoursMinutes(value) : value)} />
        {sortedWorkcells.map((workcell) => (
          <Bar
            key={workcell}
            dataKey={(entry) => (entry[workcell] > 0 ? entry[workcell] : null)}
            name={workcellNames[workcell]}
            fill={getWorkcellColor(workcell, theme.palette.chart.colors, allWorkcellIds)}
            stackId="a"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default DailyHoursWorkedChart;
